/**
 * UserBooking Page
 * Allows users to browse astrologers and book consultation slots
 * Calendar v3 - Fixed empty page issue
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { Person, Star } from '@mui/icons-material';
import DatePicker from '../components/common/DatePicker';
import AvailableSlotList from '../components/booking/AvailableSlotList';
import BookSlotDialog from '../components/booking/BookSlotDialog';
import {
  setSelectedDate,
  setSelectedAstrologer,
  fetchAstrologers,
  fetchAvailableDates,
  fetchAvailability
} from '../store/slices/availabilitySlice';
import { resetBookingStatus } from '../store/slices/bookingSlice';
import { formatDateForAPI, formatDateForDisplay } from '../utils/dateUtils';
import { useAnalytics } from '../analytics/hooks';

const UserBooking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { astrologerId } = useParams();
  const { track } = useAnalytics();

  const {
    astrologers,
    selectedAstrologer,
    selectedDate,
    availableDates,
    availableSlots,
    loading
  } = useSelector(state => state.availability);

  const { bookingStatus } = useSelector(state => state.booking);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // DEBUG LOGS - Version FIXED-v2
  console.log('[UserBooking] RENDER - Version: FIXED-v2');
  console.log('[UserBooking] astrologerId from URL:', astrologerId);
  console.log('[UserBooking] selectedAstrologer from Redux:', selectedAstrologer);
  console.log('[UserBooking] astrologers count:', astrologers.length);
  console.log('[UserBooking] loading:', loading);

  useEffect(() => {
    track('booking_started', { astrologer_id: astrologerId ? parseInt(astrologerId, 10) : null });

    // Always fetch astrologers list (needed for displaying astrologer details)
    dispatch(fetchAstrologers());

    // If astrologerId is provided in URL, set it as selected
    if (astrologerId) {
      dispatch(setSelectedAstrologer(parseInt(astrologerId, 10)));
    }
  }, [dispatch, astrologerId, track]);

  useEffect(() => {
    // Fetch available dates when astrologer is selected
    if (selectedAstrologer) {
      const today = formatDateForAPI(new Date());
      const maxDate = formatDateForAPI(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
      dispatch(fetchAvailableDates({
        astrologerId: selectedAstrologer,
        startDate: today,
        endDate: maxDate
      }));
    }
  }, [dispatch, selectedAstrologer]);

  useEffect(() => {
    // Fetch available slots when date is selected
    if (selectedAstrologer && selectedDate) {
      dispatch(fetchAvailability({
        astrologerId: selectedAstrologer,
        date: selectedDate
      }));
    }
  }, [dispatch, selectedAstrologer, selectedDate]);

  useEffect(() => {
    // Handle booking success
    if (bookingStatus === 'success') {
      track('booking_completed', { astrologer_id: selectedAstrologer });
      setSnackbar({
        open: true,
        message: 'Booking confirmed! Redirecting to your bookings...',
        severity: 'success'
      });
      dispatch(resetBookingStatus());
      setTimeout(() => {
        navigate('/my-bookings');
      }, 2000);
    }
  }, [bookingStatus, dispatch, navigate]);

  const handleAstrologerSelect = (astrologer) => {
    console.log('[UserBooking] Astrologer selected:', astrologer);
    dispatch(setSelectedAstrologer(astrologer.id));
  };

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = formatDateForAPI(date);
      dispatch(setSelectedDate(formattedDate));
      track('booking_slot_selected', { astrologer_id: selectedAstrologer, slot_date: formattedDate });
      console.log('[UserBooking] Date changed to:', formattedDate);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getHighlightedDates = () => {
    return availableDates.map(d => d.date);
  };

  const renderAstrologerCard = (astrologer) => {
    const isSelected = selectedAstrologer === astrologer.id;

    return (
      <Grid item xs={12} sm={6} md={4} key={astrologer.id}>
        <Card
          sx={{
            border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            },
          }}
          onClick={() => handleAstrologerSelect(astrologer)}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Person sx={{ mr: 1, fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">
                  {astrologer.name || 'Astrologer'}
                </Typography>
                {astrologer.specialization && (
                  <Typography variant="caption" color="textSecondary">
                    {astrologer.specialization}
                  </Typography>
                )}
              </Box>
            </Box>
            {astrologer.rating && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Star sx={{ color: '#FFC107', fontSize: 16, mr: 0.5 }} />
                <Typography variant="body2">
                  {astrologer.rating} / 5.0
                </Typography>
              </Box>
            )}
            {astrologer.experience_years && (
              <Typography variant="body2" color="textSecondary">
                {astrologer.experience_years} years experience
              </Typography>
            )}
          </CardContent>
          <CardActions>
            <Button
              size="small"
              variant={isSelected ? 'contained' : 'outlined'}
              fullWidth
            >
              {isSelected ? 'Selected' : 'Select'}
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  console.log('[UserBooking] Render conditions:');
  console.log('[UserBooking] - First block (!astrologerId && !selectedAstrologer):', !astrologerId && !selectedAstrologer);
  console.log('[UserBooking] - Second block (selectedAstrologer || astrologerId):', selectedAstrologer || astrologerId);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {!astrologerId && !selectedAstrologer && (
        <Paper sx={{ p: 4, mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Book a Consultation
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Select an astrologer to view their availability
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {astrologers.map(astrologer => renderAstrologerCard(astrologer))}
            </Grid>
          )}

          {!loading && astrologers.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No astrologers available at this time
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {(selectedAstrologer || astrologerId) && (
        <Paper sx={{ p: 4 }}>
          {astrologerId && (
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/book-consultation')}
                sx={{ mb: 2 }}
              >
                ← Back to Astrologers
              </Button>
            </Box>
          )}
          <Typography variant="h4" gutterBottom>
            Select Date & Time
          </Typography>
          {astrologerId && astrologers.length > 0 && (
            <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
              {astrologers.find(a => a.id === selectedAstrologer)?.name || 'Astrologer'}
            </Typography>
          )}
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Choose a date to see available time slots
          </Typography>

          <Box sx={{ mb: 4 }}>
            <DatePicker
              value={selectedDate ? new Date(selectedDate) : null}
              onChange={handleDateChange}
              label="Select Date"
              highlightedDates={getHighlightedDates()}
            />
          </Box>

          {selectedDate && (
            <>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Available slots for {formatDateForDisplay(selectedDate)}
              </Typography>
              <AvailableSlotList slots={availableSlots} loading={loading} />
            </>
          )}

          {!selectedDate && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                Select a date above to view available time slots
              </Typography>
              {availableDates.length > 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {availableDates.length} date{availableDates.length !== 1 ? 's' : ''} with availability
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      )}

      <BookSlotDialog />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserBooking;
